import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import Sidebar from './components/Sidebar';
import ChatArea from './components/ChatArea';
import ProfilePanel from './components/ProfilePanel';
import { getMe, getChats, getMessages, sendMessage, getUserData } from './services/api';
import websocket from './services/websocket';

function App() {
  const [user, setUser] = useState(null);
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [usersData, setUsersData] = useState({});
  
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadUser = useCallback(async () => {
    try {
      const res = await getMe();
      setUser(res.data);
      websocket.connect(res.data.username);
      loadChats();
    } catch (err) {
      console.error('Ошибка загрузки пользователя:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadChats = useCallback(async () => {
    try {
      const res = await getChats();
      setChats(res.data || []);
      
      const uniqueUsers = [...new Set((res.data || []).map(c => c.username))];
      for (const username of uniqueUsers) {
        try {
          const userRes = await getUserData(username);
          setUsersData(prev => ({
            ...prev,
            [username]: userRes.data
          }));
        } catch (err) {
          console.error('Ошибка загрузки данных пользователя:', username);
        }
      }
    } catch (err) {
      console.error('Ошибка загрузки чатов:', err);
      setChats([]);
    }
  }, []);

  useEffect(() => {
    loadUser();

    websocket.onMessage((data) => {
      console.log('📩 Получено сообщение через WebSocket:', data);
      
      if (data.type === 'status') {
        console.log('🟢 Онлайн пользователи:', data.online);
        setOnlineUsers(data.online || []);
        return;
      }
      
      if (currentChat && (data.from === currentChat.username || data.to === currentChat.username)) {
        setMessages(prev => [...prev, data]);
      }
      loadChats();
    });

    return () => {
      websocket.disconnect();
    };
  }, [currentChat, loadUser, loadChats]);

  const openChat = async (username) => {
    setCurrentChat({ username });
    if (isMobile) setShowSidebar(false);
    try {
      const res = await getMessages(username);
      setMessages(res.data || []);
      
      try {
        const userRes = await getUserData(username);
        setUsersData(prev => ({
          ...prev,
          [username]: userRes.data
        }));
      } catch (err) {
        console.error('Ошибка загрузки данных пользователя:', err);
      }
    } catch (err) {
      console.error('Ошибка загрузки сообщений:', err);
      setMessages([]);
    }
  };

  const handleSendMessage = async (text) => {
    if (!currentChat || !text || !text.trim()) {
      console.log('❌ Нет чата или пустое сообщение');
      return;
    }

    if (!user) {
      console.log('❌ Пользователь не авторизован');
      return;
    }

    const trimmedText = text.trim();
    const recipient = currentChat.username;
    
    console.log('📤 Отправка сообщения от', user.username, 'к', recipient, ':', trimmedText);
    
    try {
      const response = await sendMessage(recipient, trimmedText);
      console.log('✅ Сообщение отправлено, ответ:', response.data);
    } catch (err) {
      console.error('❌ Ошибка отправки сообщения:', err);
      if (err.response) {
        console.error('Ответ сервера:', err.response.data);
        alert('Ошибка отправки: ' + (err.response.data?.error || 'Неизвестная ошибка'));
      } else {
        alert('Ошибка отправки: ' + err.message);
      }
    }
  };

  const isUserOnline = (username) => {
    return onlineUsers.includes(username);
  };

  if (loading) return <div className="loading">Загрузка...</div>;

  return (
    <div className="app-container">
      {isMobile && (
        <div className={`mobile-menu-buttons ${(currentChat || showSidebar || showProfile) ? 'hidden' : ''}`}>
            <button className="menu-btn" onClick={() => setShowSidebar(!showSidebar)}>💬</button>
            <button className="menu-btn" onClick={() => setShowProfile(!showProfile)}>👤</button>
        </div>
        )}

      <div className={`sidebar ${isMobile ? 'mobile-sidebar' : ''} ${showSidebar ? 'mobile-open' : ''}`}>
        {isMobile && (
          <button className="mobile-back-btn" onClick={() => setShowSidebar(false)}>
            ← Назад
          </button>
        )}
        <Sidebar 
          user={user}
          chats={chats}
          currentChat={currentChat}
          onOpenChat={openChat}
          onLogin={loadUser}
          onlineUsers={onlineUsers}
          isUserOnline={isUserOnline}
          usersData={usersData}
          isMobile={isMobile}
        />
      </div>

      <div className="chat-area-wrapper">
        <ChatArea 
          currentChat={currentChat}
          messages={messages}
          onSendMessage={handleSendMessage}
          user={user}
          onCloseChat={() => setCurrentChat(null)}
          isUserOnline={isUserOnline}
          usersData={usersData}
          isMobile={isMobile}
          onOpenSidebar={() => setShowSidebar(true)}
        />
      </div>

      <div className={`profile-panel ${isMobile ? 'mobile-profile' : ''} ${showProfile ? 'mobile-open' : ''}`}>
        {isMobile && (
          <button className="mobile-back-btn" onClick={() => setShowProfile(false)}>
            ← Назад
          </button>
        )}
        <ProfilePanel 
          user={user} 
          onLogout={loadUser}
          onUpdateUser={loadUser}
        />
      </div>
    </div>
  );
}

export default App;